// ── 미팅지도 ⚙️ 설정 '마커 표시' 탭 (2026-10-24) ──────────────────────────────
// 사용자: 설정 화면은 계약관리 설정처럼 / 조각 = 상호·미팅시간·카테고리 / 테넌트 전체.
//   카테고리 줄마다 [표시 글자 입력칸][색 칸(누르면 색 칩)] — "영화 입력창 색깔창", 마커 색은 카테고리 색뿐
//   ("외부직원은 필요없는거야" — 색 기준 칸 없앰, 결정 a). 왼쪽에서 정하고 오른쪽 미리보기로 바로 본다.
import React, { useEffect } from 'react';
import { Switch } from '../contractmanagement/ContractFieldSettingsRow';
import {
  MARKER_PART_LABELS, MARKER_TIME_FORMATS, categoryMarkerText, categoryTone,
} from '../../../components/meetingmap/markerConfig';
import { useMarkerSettings } from './useMarkerSettings';
import { MarkerPreview } from './MarkerPreview';
import { CategoryColorButton } from './CategoryColorButton';

const PART_DESC = {
  name: '앞에서부터 정한 글자 수만',
  time: '숫자 14:00: 2시→14:00, 2시이후 등은 그대로',
  category: '콜 상품의 카테고리',
};
// 카테고리 칸은 색 칩이 상자 밖으로 펼쳐지므로 overflow-hidden 을 쓰지 않는다
const boxCls = 'rounded-xl border border-[var(--border)]';
const headCls = 'flex items-center gap-2 rounded-t-xl border-b border-[var(--border)] bg-[var(--bg-subtle)] px-3 py-2 text-[13px] font-bold text-[var(--text)]';
const selectCls = 'rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-1.5 py-[2px] text-[11.5px] text-[var(--text)]';
const moveBtnCls = 'h-5 w-5 rounded-md border border-[var(--border)] bg-[var(--bg-card)] text-[11px] leading-none text-[var(--text-sub)] disabled:opacity-30';

export function SettingsMarkerPanel() {
  const s = useMarkerSettings();
  const { config } = s;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { s.fetchConfig(); }, []);
  const categoryPart = config.parts.find((p) => p.key === 'category');

  if (s.loading) return <div className="py-6 text-center text-[12px] text-[var(--text-muted)]">불러오는 중…</div>;

  return (
    <div className="flex flex-col gap-3">
      <div className="text-[11px] leading-relaxed text-[var(--text-muted)]">
        지도 마커에 <strong>어떤 글자를 어떤 순서로</strong> 보일지, 카테고리마다 <strong>무슨 글자·색</strong>으로 보일지 정합니다.
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] lg:items-start">
        <div className="flex flex-col gap-3">
          {/* 마커 글자 — 조각 켜기·순서·옵션 */}
          <div className={boxCls}>
            <div className={headCls}>마커 글자</div>
            <div className="flex flex-col gap-1.5 p-2">
              {config.parts.map((p, idx) => (
                <div key={p.key} className={`flex items-center gap-2 rounded-lg bg-[var(--bg-subtle)] px-2 py-1.5 ${p.on ? '' : 'opacity-50'}`}>
                  <Switch id={`mk-${p.key}`} checked={p.on} label={`${MARKER_PART_LABELS[p.key]} 보이기`}
                    onChange={(v) => s.patchPart(p.key, { on: v })} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] text-[var(--text)]">{MARKER_PART_LABELS[p.key]}</div>
                    <div className="text-[10.5px] text-[var(--text-muted)]">{PART_DESC[p.key]}</div>
                  </div>
                  {p.key === 'name' && (
                    <select aria-label="상호 글자 수" className={selectCls} value={p.chars}
                      onChange={(e) => s.patchPart('name', { chars: Number(e.target.value) })}>
                      {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n}글자</option>)}
                    </select>
                  )}
                  {p.key === 'time' && (
                    <select aria-label="미팅시간 모양" className={selectCls} value={p.format || 'raw'}
                      onChange={(e) => s.patchPart('time', { format: e.target.value })}>
                      {MARKER_TIME_FORMATS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>
                  )}
                  {p.key === 'category' && (
                    <select aria-label="카테고리 표시" className={selectCls} value={p.mode}
                      onChange={(e) => s.patchPart('category', { mode: e.target.value })}>
                      <option value="first">앞 1글자</option>
                      <option value="full">이름 전체</option>
                    </select>
                  )}
                  <span className="inline-flex gap-[2px]">
                    {[['↑', -1, idx === 0], ['↓', 1, idx === config.parts.length - 1]].map(([sym, dir, off]) => (
                      <button key={sym} type="button" disabled={off} className={moveBtnCls}
                        aria-label={`${MARKER_PART_LABELS[p.key]} ${dir < 0 ? '앞으로' : '뒤로'}`}
                        onClick={() => s.movePart(p.key, dir)}>{sym}</button>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* 카테고리마다 표시 글자 + 색 — 예: 제주 [항] [●▾] */}
          <div className={boxCls}>
            <div className={headCls}>카테고리별 표시 글자·색</div>
            <div className="flex flex-col gap-1.5 p-2">
              <div className="px-1 text-[10.5px] leading-relaxed text-[var(--text-muted)]">
                글자는 1~3글자(비우면 {categoryPart?.mode === 'full' ? '이름 전체' : '앞 1글자'}), 색은 동그라미를 눌러 고릅니다(안 고르면 파랑).
              </div>
              {s.categories.length === 0 && <div className="px-1 text-[11.5px] text-[var(--text-muted)]">이 테넌트에 상품 카테고리가 없습니다.</div>}
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {s.categories.map((cat) => (
                  <div key={cat} className="flex items-center gap-2 rounded-lg bg-[var(--bg-subtle)] px-2 py-1.5">
                    <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--text)]" title={cat}>{cat}</span>
                    <input type="text" maxLength={3} aria-label={`${cat} 표시 글자`}
                      className="w-14 rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-1.5 py-[2px] text-center text-[12px] text-[var(--text)]"
                      placeholder={categoryMarkerText(cat, { mode: categoryPart?.mode }, {})}
                      value={config.categoryLetters[cat] || ''}
                      onChange={(e) => s.setLetter(cat, e.target.value)} />
                    <CategoryColorButton category={cat} tone={categoryTone(cat, config)}
                      onPick={(tone) => s.setCategoryColor(cat, tone)} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <MarkerPreview config={config} categories={s.categories} />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[var(--border)] pt-3">
        {s.dirty && <span className="mr-auto text-[11.5px] font-bold text-amber-600">아직 저장 전입니다</span>}
        <button type="button" onClick={s.resetDefault}
          className="h-9 rounded-lg border border-[var(--border)] bg-[var(--bg-subtle)] px-3 text-[12px] font-bold text-[var(--text-sub)]">
          처음 모양으로
        </button>
        <button type="button" onClick={s.save} disabled={s.saving || !s.dirty}
          className="h-9 rounded-lg bg-slate-900 px-4 text-[12px] font-black text-white disabled:opacity-50 dark:bg-accent-600 dark:hover:bg-accent-500">
          {s.saving ? '저장 중...' : '저장'}
        </button>
      </div>
    </div>
  );
}
